/* ============================================================
   O recorte de tempo do painel

   Antes isto era um número solto — 7, 30 ou 90 — que significava
   "os N documentos mais recentes". Funcionava enquanto as três opções
   fossem as únicas, e não dava para expressar duas coisas que o painel
   passou a precisar: **só hoje** e **um intervalo escolhido a dedo**.

   Aqui o recorte é sempre um INTERVALO FECHADO de dias, com as duas
   pontas incluídas. "Últimos 30 dias" é um intervalo como qualquer
   outro; o que o `tipo` guarda é só de onde ele veio, para a tela saber
   qual botão está aceso e que rótulo escrever.

   A DIFERENÇA QUE ISSO INTRODUZ, e ela é real: a consulta antiga pegava
   os 30 documentos mais recentes, que podiam cobrir 34 dias se a rotina
   falhasse em quatro noites. A nova pega os documentos DENTRO da janela
   de 30 dias — se faltar noite, vêm menos pontos. O gráfico passa a
   mostrar o buraco em vez de escondê-lo encostando os dias vizinhos, e
   "últimos 30 dias" passa a querer dizer trinta dias de calendário.

   Tudo aqui é função pura sobre a string "AAAA-MM-DD" — o mesmo formato
   que o app usa como chave de dia no projeto inteiro. `hoje` entra como
   argumento em vez de ser lido do relógio: é o que permite testar o
   virar do mês e o ano bissexto sem esperar por eles.
   ============================================================ */

/**
 * De onde o intervalo veio.
 *
 * `hoje` e `ultimos` são atalhos que a tela acende sozinha; `intervalo`
 * é o que a pessoa escolheu no calendário. Os três são intervalos —
 * isto aqui não muda o que é lido, muda o que a tela escreve.
 */
export type TipoDePeriodo = "hoje" | "ultimos" | "intervalo";

export interface Periodo {
  tipo: TipoDePeriodo;
  /** Primeiro dia, INCLUSIVE. "AAAA-MM-DD". */
  de: string;
  /** Último dia, INCLUSIVE. */
  ate: string;
}

/**
 * Teto do intervalo escolhido à mão.
 *
 * Um ano e um dia — dá para comparar um ano fechado com o anterior sem
 * dar para pedir a década inteira por engano. Cada dia é um documento
 * lido, e um intervalo sem teto é uma conta de Firestore sem teto.
 */
export const DIAS_MAXIMOS = 366;

/**
 * Teto de quantos dias o servidor refaz numa chamada só.
 *
 * Não é escolha desta tela: `nhomiCalcularMetricas` grampeia em 30
 * (`Math.min(..., 30)`). O painel precisa saber disso porque antes ele
 * escrevia "Refazer 90 dias" num botão que refazia 30 — a promessa era
 * do rótulo, não do servidor.
 */
export const DIAS_MAXIMOS_REFAZER = 30;

const MS_DO_DIA = 86_400_000;

/** "AAAA-MM-DD", e uma data que existe de verdade. */
export function ehDia(s: unknown): s is string {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  /* O formato passar não garante que o dia existe: "2026-02-31" e
     "2026-13-01" casam com a expressão. Voltar pela formatação é o que
     pega os dois — data que não existe vira `NaN` no `Date.parse`, e
     `paraDia` devolve "", que nunca bate com a entrada. */
  return paraDia(emMs(s)) === s;
}

/* A conta é toda em UTC de propósito. Em horário local, somar 24h atravessa
   a virada do horário de verão e devolve o mesmo dia ou pula um — um bug
   que aparece duas vezes por ano e some sozinho antes de alguém achar. */
const emMs = (dia: string): number => Date.parse(`${dia}T00:00:00.000Z`);

/* Devolve "" em vez de estourar quando a data não existe. `toISOString`
   de um `NaN` lança `RangeError`, e uma data podre vinda de um campo de
   formulário derrubaria a tela inteira em vez de virar um recado. */
const paraDia = (ms: number): string =>
  Number.isNaN(ms) ? "" : new Date(ms).toISOString().slice(0, 10);

/** O dia N dias depois (ou antes, com N negativo). "" se o dia não vale. */
export function somarDias(dia: string, n: number): string {
  return paraDia(emMs(dia) + n * MS_DO_DIA);
}

/**
 * Quantos dias o intervalo tem, contando as duas pontas.
 *
 * Um dia só é 1, não 0 — é a contagem que a tela escreve ("7 dias") e a
 * que o Firestore precisa como teto de leitura. Intervalo invertido ou
 * data inválida devolve 0, que é o jeito de dizer "não leia nada".
 */
export function contarDias(de: string, ate: string): number {
  if (!ehDia(de) || !ehDia(ate)) return 0;
  const n = Math.round((emMs(ate) - emMs(de)) / MS_DO_DIA) + 1;
  return n > 0 ? n : 0;
}

export const diasDoPeriodo = (p: Periodo): number => contarDias(p.de, p.ate);

/** Cada dia do intervalo, do mais antigo ao mais recente. */
export function diasDoIntervalo(de: string, ate: string): string[] {
  const quantos = contarDias(de, ate);
  const saida: string[] = [];
  for (let i = 0; i < quantos; i++) saida.push(somarDias(de, i));
  return saida;
}

/* ---------- os três jeitos de montar um ---------- */

export const soHoje = (hoje: string): Periodo =>
  ({ tipo: "hoje", de: hoje, ate: hoje });

/**
 * Os últimos N dias, com hoje dentro.
 *
 * `ultimosDias(1)` e `soHoje()` cobrem o mesmo dia; o que muda é o
 * rótulo. Ter os dois evita que "Hoje" apareça escrito "1 dia".
 */
export const ultimosDias = (n: number, hoje: string): Periodo => ({
  tipo: "ultimos",
  de: somarDias(hoje, -(Math.max(1, Math.trunc(n)) - 1)),
  ate: hoje,
});

export const intervalo = (de: string, ate: string): Periodo =>
  ({ tipo: "intervalo", de, ate });

/* ---------- o que a tela precisa perguntar ---------- */

/**
 * O problema do intervalo escolhido, em uma frase — ou "" se ele vale.
 *
 * Frase, e não `false`: quem escolheu 3 de março a 2 de março precisa
 * saber qual das duas datas mexer, e "intervalo inválido" não diz.
 */
export function explicarIntervalo(de: string, ate: string): string {
  if (!ehDia(de) || !ehDia(ate)) return "Escolha as duas datas.";
  if (emMs(de) > emMs(ate)) {
    return "A primeira data vem depois da última. Troque as duas de lugar.";
  }
  const dias = contarDias(de, ate);
  if (dias > DIAS_MAXIMOS) {
    return `São ${dias} dias, e o teto é ${DIAS_MAXIMOS} — cada dia é uma ` +
      "leitura. Escolha uma janela menor.";
  }
  return "";
}

/**
 * Quantos dias o servidor tem que refazer para cobrir este período.
 *
 * `nhomiCalcularMetricas` conta PARA TRÁS a partir de hoje, então cobrir
 * um intervalo que terminou semana passada custa refazer até hoje. O
 * número volta grampeado no teto do servidor — é ele que vai no rótulo
 * do botão, para o botão dizer o que vai acontecer de verdade.
 */
export function diasParaRefazer(p: Periodo, hoje: string): number {
  const bruto = contarDias(p.de, hoje);
  if (!bruto) return 0;
  return Math.min(bruto, DIAS_MAXIMOS_REFAZER);
}

/**
 * O período couber num dia só? Então o servidor sabe refazer ele
 * direto, pelo parâmetro `dia`, em vez de refazer tudo desde hoje.
 *
 * É o que torna possível consertar um dia de um mês atrás — coisa que o
 * painel não sabia fazer enquanto o recorte era "os últimos N".
 */
export const diaUnico = (p: Periodo): string | null =>
  p.de === p.ate && ehDia(p.de) ? p.de : null;

/* ---------- rótulos ---------- */

const ddmm = (dia: string) => {
  const [, m, d] = dia.split("-");
  return d && m ? `${d}/${m}` : dia;
};

/**
 * O nome curto do período — cabe num botão.
 *
 * O intervalo escolhido vira "01/09 – 12/09" em vez de "12 dias": a
 * pergunta de quem escolheu a dedo é "é a janela que eu pedi?", e o
 * total de dias não responde isso.
 */
export function rotuloCurto(p: Periodo): string {
  if (p.tipo === "hoje") return "Hoje";
  if (p.tipo === "ultimos") return `${diasDoPeriodo(p)} dias`;
  return p.de === p.ate ? ddmm(p.de) : `${ddmm(p.de)} – ${ddmm(p.ate)}`;
}
